import { useEffect, useMemo, useState } from "react";
import {
    AlertCircle,
    DollarSign,
    ExternalLink,
    Eye,
    MessageCircle,
    MousePointerClick,
    Send,
    Users,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { useNodeDetail, useStuckLeads } from "../api/queries";
import { useDashboardStore } from "../store/dashboardStore";
import { SectionCard } from "../components/SectionCard";
import { BadgeHi } from "../components/BadgeHi";
import { Paginator } from "../components/Paginator";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import { fmt, fmtPct } from "../lib/format";
import { correctFailureRate } from "../lib/numbers";
import type { StuckLead } from "../types/reports";
import { cn } from "@/lib/utils";

const STUCK_PAGE_SIZE = 20;

type BadgeColor =
    | "gray"
    | "blue"
    | "emerald"
    | "amber"
    | "rose"
    | "violet"
    | "slate";

function nodeColorByType(t?: string): BadgeColor {
    if (!t) return "gray";
    const s = t.toLowerCase();
    if (s.includes("trigger")) return "violet";
    if (
        s.includes("message") ||
        s.includes("photo") ||
        s.includes("video") ||
        s.includes("audio")
    )
        return "amber";
    if (s.includes("tag")) return "emerald";
    if (s.includes("go_to") || s.includes("goto")) return "rose";
    return "gray";
}

function isImageUrl(url?: string | null, type?: string | null) {
    if (type === "image") return true;
    if (type === "video" || type === "audio") return false;
    return !!url && /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);
}

function isVideoUrl(url?: string | null, type?: string | null) {
    if (type === "video") return true;
    if (type === "image" || type === "audio") return false;
    return !!url && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
}

function relTime(iso?: string | null) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return "agora";
    if (diffMin < 60) return diffMin + "min";
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return diffHr + "h";
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return diffDay + "d";
    return d.toISOString().slice(0, 10);
}

export function NodeDrawer() {
    const openNode = useDashboardStore((s) => s.openNode);
    const setOpenNode = useDashboardStore((s) => s.setOpenNode);

    return (
        <Sheet
            open={!!openNode}
            onOpenChange={(open) => {
                if (!open) setOpenNode(null);
            }}
        >
            <SheetContent
                side="right"
                className="w-full sm:max-w-2xl p-0 overflow-hidden flex flex-col gap-0"
            >
                {openNode && (
                    <NodeDrawerContent
                        flowId={openNode.flowId}
                        nodeId={openNode.nodeId}
                    />
                )}
            </SheetContent>
        </Sheet>
    );
}

interface NodeDrawerContentProps {
    flowId: string;
    nodeId: string;
}

function NodeDrawerContent({ flowId, nodeId }: NodeDrawerContentProps) {
    const [stuckPage, setStuckPage] = useState(1);

    // reset paginação ao trocar de node
    useEffect(() => {
        setStuckPage(1);
    }, [flowId, nodeId]);

    const detail = useNodeDetail(flowId, nodeId);
    const stuck = useStuckLeads(flowId, nodeId, {
        limit: STUCK_PAGE_SIZE,
        offset: (stuckPage - 1) * STUCK_PAGE_SIZE,
    });

    const data = detail.data;
    const loading = detail.isLoading && !detail.data;
    const error = detail.error;

    const n = data?.node;
    const buttons = data?.buttons ?? [];
    const preview = data?.preview;
    const sendsTimeseries = data?.sendsTimeseries ?? [];

    const isMsg =
        n?.type &&
        (n.type.toLowerCase().includes("message") ||
            n.type.toLowerCase().includes("photo") ||
            n.type.toLowerCase().includes("video") ||
            n.type.toLowerCase().includes("audio"));

    const revenue = n?.revenue ?? 0;
    // Backend manda `sends`/`failures` como number ou null (null = sem
    // dispatch_events no período). Para o gráfico/% trato como 0.
    const sends = (typeof n?.sends === "number" ? n.sends : 0) ?? 0;
    const failures =
        (typeof n?.failures === "number" ? n.failures : 0) ?? 0;
    const clicks =
        (typeof n?.clicks === "number" ? n.clicks : 0) ?? 0;
    // `stuck` é o nome real do backend; `stuckLeads` é alias legado.
    const stuckCount = n?.stuck ?? n?.stuckLeads ?? 0;
    const ctr = n?.ctr;
    const failureRate = correctFailureRate(failures, sends);

    const stuckItems: StuckLead[] = stuck.data?.items ?? [];
    const stuckTotal = stuck.data?.total ?? 0;
    const stuckTotalPages = Math.max(
        1,
        Math.ceil(stuckTotal / STUCK_PAGE_SIZE)
    );

    // Envios × falhas: 2 barras agregadas. Prioriza `data.sendsVsFailures`
    // se vier (compat legada), senão soma `sendsTimeseries` ou usa os
    // totais do `node` direto. O gráfico fica vazio só quando AMBOS são 0.
    const sfData = useMemo(() => {
        const legacy = data?.sendsVsFailures;
        if (legacy && legacy.length > 0) {
            return legacy.map((s) => ({
                label: s.label,
                value: s.value,
                color: /falh/i.test(s.label)
                    ? "#f43f5e"
                    : /env/i.test(s.label)
                      ? "#3b82f6"
                      : "#94a3b8",
            }));
        }
        // Soma o timeseries (mesma fonte do gráfico do flow). Fallback:
        // pega `n.sends/failures` diretos quando timeseries vier vazio.
        const sumSends =
            sendsTimeseries.reduce((acc, d) => acc + (d.sends || 0), 0) ||
            sends;
        const sumFailures =
            sendsTimeseries.reduce((acc, d) => acc + (d.failures || 0), 0) ||
            failures;
        if (sumSends === 0 && sumFailures === 0) return [];
        return [
            { label: "Envios", value: sumSends, color: "#3b82f6" },
            { label: "Falhas", value: sumFailures, color: "#f43f5e" },
        ];
    }, [data?.sendsVsFailures, sendsTimeseries, sends, failures]);

    return (
        <>
            {/* Header escuro */}
            <SheetHeader className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] px-6 py-5 text-white shrink-0 text-left space-y-0">
                <div className="flex items-center gap-3 min-w-0 pr-8">
                    <div className="bg-white/10 p-2 rounded-lg shrink-0">
                        <MessageCircle className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                            {n?.type && (
                                <BadgeHi color={nodeColorByType(n.type)}>
                                    {n.type}
                                </BadgeHi>
                            )}
                            <span className="text-[11px] text-white/60 font-mono tabular-nums truncate">
                                #{nodeId}
                            </span>
                        </div>
                        <SheetTitle className="text-lg font-bold truncate text-white">
                            {loading ? "Carregando…" : n?.name || "Node"}
                        </SheetTitle>
                    </div>
                </div>
            </SheetHeader>

            {/* Body */}
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-5">
                    {error && (
                        <ApiBanner
                            error={error}
                            onRetry={() => detail.refetch()}
                        />
                    )}

                    {/* Receita destaque */}
                    <div>
                        <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider">
                            Receita gerada
                        </p>
                        <p className="text-4xl font-bold text-gray-900 font-mono tabular-nums">
                            {loading ? (
                                <SkelText w="160px" />
                            ) : revenue > 0 ? (
                                "$" +
                                Number(revenue).toLocaleString("pt-BR", {
                                    maximumFractionDigits: 2,
                                })
                            ) : (
                                "—"
                            )}
                        </p>
                    </div>

                    {/* Mini KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <MiniStat
                            label="Já passaram"
                            value={loading ? null : fmt(sends)}
                            hint="envios"
                        />
                        <MiniStat
                            label="Parados agora"
                            value={loading ? null : fmt(stuckCount)}
                            tone="amber"
                        />
                        <MiniStat
                            label="Cliques"
                            value={loading ? null : fmt(clicks)}
                            hint={
                                ctr != null && ctr > 0
                                    ? `CTR ${fmtPct(ctr)}`
                                    : undefined
                            }
                            tone="blue"
                        />
                        <MiniStat
                            label="Falhas"
                            value={loading ? null : fmt(failures)}
                            hint={
                                sends > 0
                                    ? `${fmtPct(failureRate)} do total`
                                    : undefined
                            }
                            tone="rose"
                        />
                    </div>

                    {/* Envios × falhas + Cliques por botão */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SectionCard icon={Send} title="Envios × falhas">
                            {loading ? (
                                <DashSkeleton h={160} />
                            ) : sfData.length === 0 ? (
                                <EmptyMini />
                            ) : (
                                <>
                                    <div className="h-40">
                                        <ResponsiveContainer>
                                            <BarChart
                                                data={sfData}
                                                margin={{
                                                    top: 5,
                                                    right: 5,
                                                    bottom: 0,
                                                    left: -15,
                                                }}
                                            >
                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    stroke="#f1f5f9"
                                                    vertical={false}
                                                />
                                                <XAxis
                                                    dataKey="label"
                                                    tick={{
                                                        fontSize: 11,
                                                        fill: "#64748b",
                                                    }}
                                                    tickLine={false}
                                                    axisLine={false}
                                                />
                                                <YAxis
                                                    tick={{
                                                        fontSize: 10,
                                                        fill: "#94a3b8",
                                                    }}
                                                    tickLine={false}
                                                    axisLine={false}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        borderRadius: 8,
                                                        border: "1px solid #e5e7eb",
                                                        fontSize: 12,
                                                    }}
                                                    formatter={(v) => [
                                                        fmt(Number(v ?? 0)),
                                                        "Total",
                                                    ]}
                                                />
                                                <Bar
                                                    dataKey="value"
                                                    radius={[4, 4, 0, 0]}
                                                >
                                                    {sfData.map((d, i) => (
                                                        <Cell
                                                            key={i}
                                                            fill={d.color}
                                                        />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="flex items-center justify-between mt-2 text-xs">
                                        <span className="text-gray-600">
                                            enviado:{" "}
                                            <strong className="text-gray-900 font-mono tabular-nums">
                                                {fmt(sends)}
                                            </strong>
                                        </span>
                                        <span className="text-rose-600">
                                            falhou:{" "}
                                            <strong className="font-mono tabular-nums">
                                                {fmt(failures)} ·{" "}
                                                {fmtPct(failureRate)}
                                            </strong>
                                        </span>
                                    </div>
                                </>
                            )}
                        </SectionCard>

                        {buttons.length > 0 ? (
                            <SectionCard
                                icon={MousePointerClick}
                                title="Cliques por botão"
                            >
                                <div className="space-y-2">
                                    {(() => {
                                        // Em PHOTO_TEMPLATE o lead pode clicar
                                        // direto na foto (não só nos botões).
                                        // Backend conta `node.clicks` total e
                                        // `button.clicks` separado — a diferença
                                        // é o clique no card/foto.
                                        const buttonSum = buttons.reduce(
                                            (acc, b) => acc + (b.clicks || 0),
                                            0
                                        );
                                        const onCard = clicks - buttonSum;
                                        if (onCard <= 0) return null;
                                        const isPhoto = (n?.type || "")
                                            .toLowerCase()
                                            .includes("photo");
                                        return (
                                            <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50/60">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <Eye className="w-3 h-3 text-amber-500 shrink-0" />
                                                        <span className="text-sm font-semibold text-amber-900">
                                                            {isPhoto
                                                                ? "Clique na foto"
                                                                : "Clique no card"}
                                                        </span>
                                                    </div>
                                                    <span className="text-[10px] text-amber-700/70 uppercase font-medium tracking-wider">
                                                        sem botão · abre URL do card
                                                    </span>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="text-sm font-bold text-amber-700 font-mono tabular-nums">
                                                        {fmt(onCard)}
                                                    </p>
                                                    <p className="text-[10px] text-amber-700/70 uppercase tracking-wider font-medium">
                                                        cliques
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    {buttons.map((b, i) => (
                                        <div
                                            key={i}
                                            className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-gray-50 border-gray-100"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5">
                                                    {b.type === "url" && (
                                                        <ExternalLink className="w-3 h-3 text-gray-400 shrink-0" />
                                                    )}
                                                    <span className="text-sm font-semibold text-gray-800 truncate">
                                                        {b.text || "—"}
                                                    </span>
                                                </div>
                                                {b.type === "url" && b.url ? (
                                                    <a
                                                        href={b.url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        className="text-[10px] text-blue-600 hover:underline font-mono mt-0.5 truncate block"
                                                        title={b.url}
                                                    >
                                                        {b.url}
                                                    </a>
                                                ) : (
                                                    b.type && (
                                                        <span className="text-[10px] text-gray-400 uppercase font-medium tracking-wider">
                                                            {b.type}
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="text-sm font-bold text-blue-600 font-mono tabular-nums">
                                                    {fmt(b.clicks)}
                                                </p>
                                                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">
                                                    cliques
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </SectionCard>
                        ) : (
                            <SectionCard
                                icon={AlertCircle}
                                title="Sem botões"
                            >
                                <p className="text-xs text-gray-400 py-4 text-center">
                                    Este node não possui botões com cliques no
                                    período.
                                </p>
                            </SectionCard>
                        )}
                    </div>

                    {/* Leads parados */}
                    <SectionCard
                        icon={Users}
                        title={`Leads parados · ${fmt(stuckTotal)}`}
                        subtitle={
                            stuckTotal > 0
                                ? `mostrando até ${STUCK_PAGE_SIZE} por página`
                                : undefined
                        }
                    >
                        {stuck.isLoading && !stuck.data ? (
                            <DashSkeleton h={180} />
                        ) : !stuckItems.length ? (
                            <p className="text-xs text-gray-400 py-6 text-center">
                                Nenhum lead parado no momento.
                            </p>
                        ) : (
                            <>
                                <div className="overflow-x-auto -mx-5">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-[10px] uppercase text-gray-500 tracking-wider border-b border-gray-200">
                                                <th className="text-left px-5 py-2">
                                                    Lead
                                                </th>
                                                <th className="text-right px-5 py-2">
                                                    Chegou
                                                </th>
                                                <th className="text-right px-5 py-2">
                                                    Últ. inter.
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {stuckItems.map((l) => (
                                                <tr
                                                    key={l.leadId}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <td className="px-5 py-2 font-medium text-gray-800">
                                                        {l.name ||
                                                            l.leadId ||
                                                            "—"}
                                                    </td>
                                                    <td className="px-5 py-2 text-right text-xs font-mono tabular-nums text-gray-500">
                                                        {relTime(l.enteredAt)}
                                                    </td>
                                                    <td className="px-5 py-2 text-right text-xs font-mono tabular-nums text-gray-500">
                                                        {relTime(l.lastSeen)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-3">
                                    <Paginator
                                        page={stuckPage}
                                        totalPages={stuckTotalPages}
                                        totalItems={stuckTotal}
                                        pageSize={STUCK_PAGE_SIZE}
                                        loading={stuck.isFetching}
                                        onChange={setStuckPage}
                                    />
                                </div>
                            </>
                        )}
                    </SectionCard>

                    {/* Preview do copy */}
                    {isMsg && preview && (
                        <SectionCard icon={Eye} title="Preview · copy do node">
                            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg p-4">
                                <div className="flex items-end gap-2 max-w-md">
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shrink-0" />
                                    <div className="bg-white rounded-2xl rounded-bl-md shadow-sm border border-gray-100 overflow-hidden max-w-[320px] w-full">
                                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-2">
                                            <p className="text-xs font-bold text-gray-800 truncate">
                                                {n?.name || "Node"}
                                            </p>
                                            {n?.type && (
                                                <span className="text-[9px] uppercase font-semibold tracking-wider text-gray-400 shrink-0">
                                                    {n.type}
                                                </span>
                                            )}
                                        </div>
                                        {preview.mediaUrl &&
                                            isImageUrl(
                                                preview.mediaUrl,
                                                preview.mediaType
                                            ) && (
                                                <img
                                                    src={preview.mediaUrl}
                                                    alt=""
                                                    className="w-full max-h-64 object-cover"
                                                    onError={(e) => {
                                                        (
                                                            e.currentTarget as HTMLImageElement
                                                        ).style.display =
                                                            "none";
                                                    }}
                                                />
                                            )}
                                        {preview.mediaUrl &&
                                            isVideoUrl(
                                                preview.mediaUrl,
                                                preview.mediaType
                                            ) && (
                                                <video
                                                    src={preview.mediaUrl}
                                                    controls
                                                    className="w-full max-h-64"
                                                />
                                            )}
                                        {(preview.title ||
                                            preview.subtitle) && (
                                            <div className="px-4 pt-3 pb-2">
                                                {preview.title && (
                                                    <p className="text-sm font-bold text-gray-900 leading-tight">
                                                        {preview.title}
                                                    </p>
                                                )}
                                                {preview.subtitle && (
                                                    <p className="text-xs text-gray-600 mt-0.5 leading-snug">
                                                        {preview.subtitle}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        {preview.text && (
                                            <div className="px-4 py-3">
                                                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                                                    {preview.text}
                                                </p>
                                            </div>
                                        )}
                                        {(preview.buttons || []).length > 0 && (
                                            <div className="border-t border-gray-100">
                                                {preview.buttons!.map(
                                                    (b, i) => (
                                                        <div
                                                            key={i}
                                                            className="px-4 py-2.5 text-center text-blue-600 text-xs font-medium border-b last:border-0 border-gray-100 truncate"
                                                        >
                                                            {b.text}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center gap-3 text-[10px] text-gray-500 font-mono tabular-nums pl-9 flex-wrap">
                                    <span>id #{nodeId}</span>
                                    {preview.mediaUrl && (
                                        <span className="text-gray-400">
                                            ·{" "}
                                            {isImageUrl(
                                                preview.mediaUrl,
                                                preview.mediaType
                                            )
                                                ? "imagem"
                                                : isVideoUrl(
                                                        preview.mediaUrl,
                                                        preview.mediaType
                                                    )
                                                  ? "vídeo"
                                                  : "mídia"}
                                        </span>
                                    )}
                                    {(preview.buttons || []).length > 0 && (
                                        <span className="text-gray-400">
                                            · {preview.buttons!.length} botão
                                            {preview.buttons!.length > 1
                                                ? "s"
                                                : ""}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </SectionCard>
                    )}

                    {/* Receita resumo (caso sem preview) */}
                    {!isMsg && revenue > 0 && (
                        <SectionCard icon={DollarSign} title="Resumo">
                            <p className="text-xs text-gray-500">
                                Este node gerou{" "}
                                <strong className="text-emerald-600 font-mono tabular-nums">
                                    $
                                    {Number(revenue).toLocaleString("pt-BR", {
                                        maximumFractionDigits: 2,
                                    })}
                                </strong>{" "}
                                no período.
                            </p>
                        </SectionCard>
                    )}
                </div>
            </div>
        </>
    );
}

interface MiniStatProps {
    label: string;
    value: React.ReactNode | null;
    hint?: string;
    tone?: "amber" | "blue" | "rose";
}

function MiniStat({ label, value, hint, tone }: MiniStatProps) {
    return (
        <div
            className={cn(
                "bg-white border rounded-lg p-3",
                tone === "amber"
                    ? "border-amber-200 ring-1 ring-amber-100"
                    : tone === "blue"
                      ? "border-blue-100"
                      : tone === "rose"
                        ? "border-rose-100"
                        : "border-gray-100"
            )}
        >
            <p className="text-[10px] text-gray-500 uppercase font-medium tracking-wider">
                {label}
            </p>
            <p
                className={cn(
                    "text-base font-bold font-mono tabular-nums mt-1",
                    tone === "amber"
                        ? "text-amber-700"
                        : tone === "blue"
                          ? "text-blue-700"
                          : tone === "rose"
                            ? "text-rose-700"
                            : "text-gray-900"
                )}
            >
                {value == null ? <SkelText w="40px" /> : value}
            </p>
            {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
        </div>
    );
}

function EmptyMini() {
    return (
        <div className="h-40 flex items-center justify-center text-xs text-gray-400">
            Sem dados
        </div>
    );
}
